/* ════════════════════════════════════════════════════════════════════
   Footer institutionnel HAC — rendu du flux partenaires
   Design system HAC §20, « Algorithme de rendu (client) »

   Le flux officiel (https://hac.football/api/partners) est authentifié par
   un Bearer token. Ce token ne doit JAMAIS arriver dans un thème Shopify,
   qui est entièrement public. Le design system prévoit exactement ce cas :
   un job planifié recopie le flux dans un fichier statique sans secret, et
   le client ne lit que ce fichier. Ici c'est le workflow
   sync-hac-partners.yml qui joue ce rôle, et le fichier est
   assets/hac-partners.json, servi par le CDN de la boutique.

   Contrat d'un partenaire :
     name       → alt / title
     logo_url   → image absolue
     website    → null = logo sans lien
     position   → ordre DANS la catégorie
     type       → libellé de la catégorie
     type_rank  → rang de la catégorie (0 = plus haut, peut être null)
   ════════════════════════════════════════════════════════════════════ */

(() => {
  'use strict';

  const SLOT_WIDTH = 200;
  const SLOT_HEIGHT = 80;

  /**
   * Regroupe les partenaires par catégorie en respectant type_rank puis
   * position. Le flux arrive déjà trié, mais on ne s'en remet pas à ça :
   * un tri côté serveur qui change ne doit pas désordonner le footer.
   */
  function groupByType(partners) {
    const groups = new Map();

    partners.forEach((partner) => {
      if (!partner || !partner.logo_url) return;
      const type = typeof partner.type === 'string' ? partner.type : '';
      if (!groups.has(type)) {
        groups.set(type, {
          type,
          // type_rank peut être null : ces catégories passent en dernier.
          rank: typeof partner.type_rank === 'number' ? partner.type_rank : Number.MAX_SAFE_INTEGER,
          items: [],
        });
      }
      groups.get(type).items.push(partner);
    });

    return Array.from(groups.values())
      .sort((a, b) => a.rank - b.rank)
      .map((group) => {
        group.items.sort((a, b) => {
          const pa = typeof a.position === 'number' ? a.position : Number.MAX_SAFE_INTEGER;
          const pb = typeof b.position === 'number' ? b.position : Number.MAX_SAFE_INTEGER;
          if (pa !== pb) return pa - pb;
          // À position égale — le flux en contient — on départage par nom
          // pour que l'ordre reste stable d'un chargement à l'autre.
          return String(a.name || '').localeCompare(String(b.name || ''), 'fr');
        });
        return group;
      });
  }

  function buildSlot(partner) {
    const img = document.createElement('img');
    img.src = partner.logo_url;
    img.alt = partner.name || '';
    img.width = SLOT_WIDTH;
    img.height = SLOT_HEIGHT;
    img.loading = 'lazy';
    img.decoding = 'async';

    // website null ou vide → logo affiché sans lien (prévu par le contrat).
    if (!partner.website) {
      const slot = document.createElement('div');
      slot.className = 'hac-footer__partner';
      slot.appendChild(img);
      return slot;
    }

    const link = document.createElement('a');
    link.className = 'hac-footer__partner';
    link.href = partner.website;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    if (partner.name) link.title = partner.name;
    link.appendChild(img);
    return link;
  }

  function render(container, partners) {
    const groups = groupByType(partners);
    if (!groups.length) return;

    const fragment = document.createDocumentFragment();

    groups.forEach((group) => {
      const section = document.createElement('div');
      section.className = 'hac-footer__partners-group';

      if (group.type) {
        const title = document.createElement('p');
        title.className = 'hac-footer__partners-title';
        title.textContent = group.type;
        section.appendChild(title);
      }

      const row = document.createElement('div');
      row.className = 'hac-footer__partners-row';
      group.items.forEach((partner) => row.appendChild(buildSlot(partner)));

      section.appendChild(row);
      fragment.appendChild(section);
    });

    container.appendChild(fragment);
  }

  async function load(container) {
    const url = container.dataset.partnersUrl;
    if (!url) return;

    try {
      const response = await fetch(url, { credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      const partners = Array.isArray(payload) ? payload : payload.partners;
      if (!Array.isArray(partners)) throw new Error('format inattendu');
      render(container, partners);
    } catch (error) {
      /* Le design system impose que la page ne casse jamais. On laisse le
         conteneur vide plutôt que d'afficher un jeu d'exemple : sur une
         boutique, de faux partenaires seraient pires qu'aucun. */
      if (window.console && console.warn) {
        console.warn('[footer HAC] flux partenaires indisponible :', error.message);
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-hac-partners]').forEach((container) => {
      if (container.dataset.hacPartnersLoaded === 'true') return;
      container.dataset.hacPartnersLoaded = 'true';
      load(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Le client re-rend la section à chaque modification dans l'éditeur de thème.
  document.addEventListener('shopify:section:load', init);
})();
